import { useMemo, useRef, useState } from 'react';
import { Box, Chip, Collapse, Divider, styled, ToggleButton, ToggleButtonGroup, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { selectTestCaseDataLocal } from '@pages/testcases/store/selectors';
import { ReactSortable } from 'react-sortablejs';
import { updateTestCaseField } from '@pages/testcases/store';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import IconButton from '@mui/material/IconButton';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import MoveDownOutlinedIcon from '@mui/icons-material/MoveDownOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ArrowUpwardOutlinedIcon from '@mui/icons-material/ArrowUpwardOutlined';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import TestcaseStepEditorField from './TestcaseStepEditorField';
import {
    generateUniqueStringKey,
    getAppContentElement,
    scrollToTopOfAppContainer,
    toTitleCase,
} from '@shared/utils/utilsOld';
import ModalDelete from '@shared/components/modals/ModalDelete';
import TestcaseStepCloneDialog from './TestcaseStepCloneDialog';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import useScrollPosition from '@shared/hooks/useScrollPosition';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelIcon from '@mui/icons-material/Cancel';
import ClearIcon from '@mui/icons-material/Clear';
import UtmsButton from '@shared/components/ui/UtmsButton';

const TestcaseThirdTab = ({
    propToRerenderEditorJs,
    dataSelector,
    checkingMode,
    viewOnly,
    testStepsState,
    setTestStepsState,
}) => {
    const testcaseDataLocal = useSelector(dataSelector || selectTestCaseDataLocal);
    const [movedStepHeightToCompensate, setMovedStepHeightToCompensate] = useState(null);
    const [isSomeStepMoving, setIsSomeStepMoving] = useState(false);
    const dispatch = useDispatch();
    const theme = useTheme();
    const id = useRef(null);
    const { t } = useTranslation();

    const setNewStepsPositions = (newSteps) => {
        if (checkingMode) {
            return;
        }

        const newStepsWithoutTmpInfo = newSteps.map((step) => {
            let newStepCopy = structuredClone(step);
            delete newStepCopy.chosen;
            delete newStepCopy.selected;

            return newStepCopy;
        });

        dispatch(updateTestCaseField({ field: 'test_scripts', newValue: newStepsWithoutTmpInfo }));
    };

    const handleStartStepMove = (e) => {
        const ghostHeight = 67;
        setMovedStepHeightToCompensate(e.item.clientHeight - ghostHeight);
    };

    const handleEndStepMove = () => {
        setMovedStepHeightToCompensate(null);
    };

    const stepsListCopy = useMemo(
        () => structuredClone(testcaseDataLocal.test_scripts),
        [testcaseDataLocal.test_scripts],
    );

    const handleChangeTestStepsState = (stepId) => (newState) => {
        switch (newState) {
            case 'passed': {
                setTestStepsState((state) => ({ ...state, [stepId]: true }));
                break;
            }
            case 'failed': {
                setTestStepsState((state) => ({ ...state, [stepId]: false }));
                break;
            }
            case 'clear': {
                setTestStepsState(({ [stepId]: _selectedValue, ...restState }) => restState);
                break;
            }
            default: {
            }
        }
    };

    const handleAddFirstStep = () => {
        const newStep = {
            id: generateUniqueStringKey('test_script'),
            script: {},
            result: {},
        };
        dispatch(updateTestCaseField({ field: 'test_scripts', newValue: [newStep] }));
    };

    if (!testcaseDataLocal.test_scripts || testcaseDataLocal.test_scripts.length === 0) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    p: 4,
                    border: '1px dashed var(--border-color)',
                    borderRadius: '6px',
                    background: 'var(--section-bg-color)',
                }}
            >
                <Typography variant="body1" color="text.secondary">
                    {t('no_test_steps_yet')}.
                </Typography>
                {!checkingMode && !viewOnly && (
                    <UtmsButton startIcon={<AddIcon />} onClick={handleAddFirstStep}>
                        {t('add_first_test_step')}
                    </UtmsButton>
                )}
            </Box>
        );
    }

    return (
        <Box
            sx={{
                userSelect: 'none',
                '& .sortable-ghost': {
                    background: theme.palette.components.navigation.background,
                },
                '& .testcase_test-step:last-child': {
                    mb: movedStepHeightToCompensate ? `${movedStepHeightToCompensate}px` : 0,
                },
                '& .sortable-chosen .testcase_test-step_drag-handle': {
                    cursor: 'grabbing',
                },
            }}
        >
            <ReactSortable
                style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
                handle=".testcase_test-step_drag-handle"
                dragClass="sortable-drop"
                list={stepsListCopy}
                setList={setNewStepsPositions}
                animation={200}
                onStart={() => {
                    if (id.current) {
                        clearTimeout(id.current);
                    }
                    setIsSomeStepMoving(true);
                }}
                onEnd={() => {
                    id.current = setTimeout(() => setIsSomeStepMoving(false), 500);
                }}
                onChoose={handleStartStepMove}
                onUnchoose={handleEndStepMove}
                disabled={testcaseDataLocal.test_scripts.length < 2 || checkingMode}
            >
                {stepsListCopy.map((step, index, array) => (
                    <TestcaseStep
                        key={step.id}
                        step={step}
                        index={index}
                        steps={array}
                        inOnlyStep={array.length < 2}
                        propToRerenderEditorJs={propToRerenderEditorJs}
                        isSomeStepMoving={isSomeStepMoving}
                        checkingMode={checkingMode}
                        viewOnly={viewOnly}
                        testStepState={testStepsState?.[step.id]}
                        handleChangeTestStepState={handleChangeTestStepsState(step.id)}
                    />
                ))}
            </ReactSortable>
        </Box>
    );
};

const TestcaseStep = ({
    step,
    index,
    steps,
    inOnlyStep,
    propToRerenderEditorJs,
    isSomeStepMoving,
    checkingMode,
    viewOnly,
    testStepState,
    handleChangeTestStepState,
}) => {
    const { t } = useTranslation();
    const [isContentHidden, setIsContentHidden] = useState(false);
    const [isStepHovered, setIsStepHovered] = useState(false);
    const [stepElementsFocusState, setStepElementsFocusState] = useState([false, false, false]); // "Action" editor, "Result" editor, toolbar
    const isToolbarShown =
        (isStepHovered && !isSomeStepMoving) || Boolean(stepElementsFocusState.filter(Boolean).length);
    const testStepCheckValue = typeof testStepState === 'boolean' ? (testStepState ? 'passed' : 'failed') : null;

    const toggleContentVisibility = () => {
        setIsContentHidden((prev) => !prev);
    };

    const handleStepMouseOver = () => {
        setIsStepHovered(true);
    };

    const handleStepMouseOut = () => {
        setIsStepHovered(false);
    };

    const handleChangeFocusState = (elementIndex, newEditorFocusState) => {
        setStepElementsFocusState((prevElementsFocusState) => {
            const newFocusState = [...prevElementsFocusState];
            newFocusState[elementIndex] = newEditorFocusState;
            return newFocusState;
        });
    };

    const handleElementFocus = (editorIndex) => () => {
        handleChangeFocusState(editorIndex, true);
    };

    const handleElementBlur = (editorIndex) => () => {
        handleChangeFocusState(editorIndex, false);
    };

    const handleChangeCheckState = (_e, newState) => {
        if (newState === null) {
            return;
        }

        handleChangeTestStepState(newState);
    };

    const handleClearCheckState = () => {
        handleChangeTestStepState('clear');
    };

    return (
        <Box
            sx={{
                background: testStepCheckValue
                    ? testStepCheckValue === 'passed'
                        ? 'var(--success-selected)'
                        : 'var(--error-selected)'
                    : 'var(--section-bg-color)',
                border: '1px solid var(--border-color)',
                borderRadius: '6px',
                p: 2,
                '& .drag-icon': {
                    opacity: 0,
                    transition: '0.15s opacity ease',
                },
                '& :hover .drag-icon': {
                    opacity: 1,
                },
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
            }}
            className="testcase_test-step"
            onMouseOver={handleStepMouseOver}
            onMouseOut={handleStepMouseOut}
        >
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <IconButton size="small" onClick={toggleContentVisibility}>
                    {isContentHidden ? <ExpandMoreIcon /> : <ExpandLessIcon />}
                </IconButton>
                <Box
                    className="testcase_test-step_drag-handle"
                    sx={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        cursor: inOnlyStep ? 'initial' : 'grab',
                        gap: 2,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '1.5rem',
                            fontWeight: 700,
                        }}
                    >
                        {`${toTitleCase(t('step'))} ${index + 1}`}
                    </Typography>
                    {checkingMode && testStepCheckValue && (
                        <Chip
                            sx={{ fontSize: '0.813rem' }}
                            size="small"
                            label={t(testStepCheckValue)}
                            color={testStepCheckValue === 'passed' ? 'success' : 'error'}
                        />
                    )}
                    {!inOnlyStep && !checkingMode && <DragIndicatorIcon className="drag-icon" />}
                </Box>
            </Box>
            {!isContentHidden && (
                <>
                    <Box
                        sx={{
                            mt: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 2,
                            pointerEvents: isSomeStepMoving ? 'none' : 'initial', // disable pointer events for EditorJs while moving, it can paste content to other step
                        }}
                        className="sortable-content-hidden"
                    >
                        <Box
                            sx={{ flex: 1, minWidth: 0 }}
                            onFocus={handleElementFocus(0)}
                            onBlur={handleElementBlur(0)}
                        >
                            <TestcaseStepEditorField
                                step={step}
                                kind="script"
                                zIndex={steps.length - index}
                                propToRerenderEditorJs={propToRerenderEditorJs}
                                checkingMode={checkingMode}
                            />
                        </Box>
                        <Divider orientation="vertical" flexItem />
                        <Box
                            sx={{ flex: 1, minWidth: 0 }}
                            onFocus={handleElementFocus(1)}
                            onBlur={handleElementBlur(1)}
                        >
                            <TestcaseStepEditorField
                                step={step}
                                kind="result"
                                zIndex={steps.length - index}
                                propToRerenderEditorJs={propToRerenderEditorJs}
                                checkingMode={checkingMode}
                            />
                        </Box>
                    </Box>
                </>
            )}
            {!checkingMode && (
                <Collapse
                    in={isToolbarShown}
                    style={{
                        transformOrigin: '0 0 0',
                    }}
                    {...(isToolbarShown ? { timeout: 300 } : {})}
                >
                    <Box
                        sx={{ mt: 2 }}
                        tabIndex={0}
                        className="sortable-content-hidden"
                        onFocus={handleElementFocus(2)}
                        onBlur={handleElementBlur(2)}
                    >
                        <TestcaseStepToolbox stepIndex={index} />
                    </Box>
                </Collapse>
            )}
            {checkingMode && !viewOnly && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: 1,
                        alignSelf: 'center',
                        position: 'relative',
                    }}
                >
                    <Typography
                        sx={{
                            position: 'absolute',
                            whiteSpace: 'nowrap',
                            right: '100px',
                            color: 'var(--text-disabled)',
                        }}
                    >{`${t('specify_result')}:`}</Typography>
                    <ToggleButtonGroup value={testStepCheckValue} exclusive onChange={handleChangeCheckState}>
                        <CustomToggleButton sx={{}} color="success" value="passed">
                            <CheckCircleOutlineIcon />
                        </CustomToggleButton>
                        <CustomToggleButton color="error" value="failed">
                            <CancelIcon />
                        </CustomToggleButton>
                    </ToggleButtonGroup>
                    {testStepCheckValue && (
                        <UtmsButton
                            startIcon={<ClearIcon />}
                            sx={{ position: 'absolute', left: '100px' }}
                            onClick={handleClearCheckState}
                        >
                            {t('clear')}
                        </UtmsButton>
                    )}
                </Box>
            )}
        </Box>
    );
};

export const CustomToggleButton = styled(ToggleButton)(({ theme }) => ({
    '&.Mui-selected[value="passed"]': {
        backgroundColor: theme.palette.success.main,
        color: '#fff',
    },
    '&.Mui-selected[value="passed"]:hover': {
        backgroundColor: theme.palette.success.main,
        color: '#fff',
    },
    '&.Mui-selected[value="failed"]': {
        backgroundColor: theme.palette.error.main,
        color: '#fff',
    },
    '&.Mui-selected[value="failed"]:hover': {
        backgroundColor: theme.palette.error.main,
    },
}));

const TestcaseStepToolbox = ({ stepIndex }) => {
    const [deleteStepModalOpen, setDeleteStepModalOpen] = useState(false);
    const [cloneStepModalOpen, setCloneStepModalOpen] = useState(false);
    const [moveMenuAnchorEl, setMoveMenuAnchorEl] = useState(null);
    const testcaseDataLocal = useSelector(selectTestCaseDataLocal);
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const scrollPosition = useScrollPosition(getAppContentElement());

    const handleAddNewStep = () => {
        const testStepsArrayCopy = structuredClone(testcaseDataLocal.test_scripts);
        testStepsArrayCopy.splice(stepIndex + 1, 0, {
            id: generateUniqueStringKey('test_script'),
            script: {},
            result: {},
        });
        dispatch(updateTestCaseField({ field: 'test_scripts', newValue: testStepsArrayCopy }));
    };

    const handleDeleteStepSubmit = () => {
        const newTestStepsArray = testcaseDataLocal.test_scripts.filter((step, index) => index !== stepIndex);
        dispatch(updateTestCaseField({ field: 'test_scripts', newValue: newTestStepsArray }));
    };

    const handleOpenDeleteStepConfirmModal = () => {
        setDeleteStepModalOpen(true);
    };

    const handleCloneStepSubmit = ({ direction, stepToPlaceIndex }) => {
        const testStepsArrayCopy = structuredClone(testcaseDataLocal.test_scripts);
        const stepObjectCopy = structuredClone(testcaseDataLocal.test_scripts[stepIndex]);
        stepObjectCopy.id = generateUniqueStringKey('test_script');

        const indexToPlace = stepToPlaceIndex + (direction === 'after' ? 1 : 0);
        testStepsArrayCopy.splice(indexToPlace, 0, stepObjectCopy);
        dispatch(updateTestCaseField({ field: 'test_scripts', newValue: testStepsArrayCopy }));
    };

    const handleOpenCloneStepModal = () => {
        setCloneStepModalOpen(true);
    };

    const handleMoveStepSubmit = (moveDirection) => () => {
        const testStepsArrayCopy = structuredClone(testcaseDataLocal.test_scripts);

        const element = testStepsArrayCopy[stepIndex];

        switch (moveDirection) {
            case 'up':
                if (stepIndex > 0) {
                    testStepsArrayCopy.splice(stepIndex, 1);
                    testStepsArrayCopy.splice(stepIndex - 1, 0, element);
                }
                break;
            case 'down':
                if (stepIndex < testStepsArrayCopy.length - 1) {
                    testStepsArrayCopy.splice(stepIndex, 1);
                    testStepsArrayCopy.splice(stepIndex + 1, 0, element);
                }
                break;
            case 'first':
                testStepsArrayCopy.splice(stepIndex, 1);
                testStepsArrayCopy.unshift(element);
                break;
            case 'last':
                testStepsArrayCopy.splice(stepIndex, 1);
                testStepsArrayCopy.push(element);
                break;
            default:
                console.error('Invalid step move direction');
        }

        dispatch(updateTestCaseField({ field: 'test_scripts', newValue: testStepsArrayCopy }));
        handleCloseMoveMenu();
    };

    const handleOpenMoveMenu = (event) => {
        setMoveMenuAnchorEl(event.currentTarget);
    };

    const handleCloseMoveMenu = () => {
        setMoveMenuAnchorEl(null);
    };

    const handleScrollToTop = () => {
        scrollToTopOfAppContainer();
    };

    return (
        <>
            <Box sx={{ display: 'flex', gap: 2 }}>
                <UtmsButton startIcon={<AddIcon />} onClick={handleAddNewStep}>
                    {t('add_new_test_step')}
                </UtmsButton>
                <UtmsButton startIcon={<ContentCopyIcon />} onClick={handleOpenCloneStepModal}>
                    {t('clone')}
                </UtmsButton>
                {testcaseDataLocal.test_scripts.length > 1 && (
                    <>
                        <UtmsButton
                            startIcon={<MoveDownOutlinedIcon />}
                            endIcon={<KeyboardArrowDownIcon />}
                            onClick={handleOpenMoveMenu}
                        >
                            {t('move')}
                        </UtmsButton>
                        <Divider orientation="vertical" flexItem />
                        <UtmsButton startIcon={<DeleteOutlinedIcon />} onClick={handleOpenDeleteStepConfirmModal}>
                            {t('delete')}
                        </UtmsButton>
                    </>
                )}
                <Collapse in={scrollPosition !== 0}>
                    <>
                        <Divider orientation="vertical" flexItem />
                        <UtmsButton startIcon={<ArrowUpwardOutlinedIcon />} onClick={handleScrollToTop}>
                            {t('scroll_to_top')}
                        </UtmsButton>
                    </>
                </Collapse>
            </Box>
            <ModalDelete
                open={deleteStepModalOpen}
                setOpen={setDeleteStepModalOpen}
                handleSubmit={handleDeleteStepSubmit}
                objectName={`${toTitleCase(t('step'))} ${stepIndex + 1}`}
                warningText={`${t('numbers_will_change_warning')} \n ${t('revert_changes_message_2')}`}
            />
            <TestcaseStepCloneDialog
                open={cloneStepModalOpen}
                setOpen={setCloneStepModalOpen}
                stepIndex={stepIndex}
                handleSubmit={handleCloneStepSubmit}
            />
            <Menu anchorEl={moveMenuAnchorEl} open={Boolean(moveMenuAnchorEl)} onClose={handleCloseMoveMenu}>
                {stepIndex !== 0 && <MenuItem onClick={handleMoveStepSubmit('up')}>{t('step_up')}</MenuItem>}
                {stepIndex !== testcaseDataLocal.test_scripts.length - 1 && (
                    <MenuItem onClick={handleMoveStepSubmit('down')}>{t('step_down')}</MenuItem>
                )}
                <Divider />
                {stepIndex !== 0 && <MenuItem onClick={handleMoveStepSubmit('first')}>{t('make_first')}</MenuItem>}
                {stepIndex !== testcaseDataLocal.test_scripts.length - 1 && (
                    <MenuItem onClick={handleMoveStepSubmit('last')}>{t('make_last')}</MenuItem>
                )}
            </Menu>
        </>
    );
};

export default TestcaseThirdTab;
